import PptxGenJS from 'pptxgenjs';
import { findUtType, utUnitOf } from '../constants';
import { MstTgtTpCd, RptSumItem, RptUtmTarget } from '../types';
import { rptSumDiff } from './rptSumDiff';

const ROLE_LABEL: Record<MstTgtTpCd, string> = { MST: '기준', TGT: '비교대상', REF: '참조' };
const ROLE_ORDER: MstTgtTpCd[] = ['REF', 'MST', 'TGT'];

const FONT = 'LG스마트체 Regular';
const HEADER_FILL = 'FBE5D6';
const BORDER_COLOR = 'E2E8F0';
const RED = 'B32542';
const GRAY = '333333';

const fmt = (v: number | null | undefined): string =>
  v === null || v === undefined ? '-' : v.toLocaleString('ko-KR', { maximumFractionDigits: 1 });

/**
 * 단위 표기의 출처는 **FE 상수(constants.ts의 UT_TYPES) 하나다.**
 *
 * 예전에는 DB의 `UTM_TP_NM`과 `MSR_UNIT_CD`를 이어 붙였는데(`${utmTpNm}${msrUnitCd}`),
 * `UTM_TP_NM`에 이미 단위가 들어 있는 경우 `CDA [Nl/min][ℓ/min]`처럼 두 번 나왔다.
 * 게다가 두 값의 출처가 달라 서로 다른 단위가 나란히 붙기도 했다.
 *
 * UT_TYPES.label이 `CDA [Nl/min]` 형태로 이름+단위를 함께 갖고 있어 헤더는 그대로 쓰고,
 * 이름과 단위를 따로 써야 하는 표에서는 label을 잘라 쓴다.
 *
 * 상수에 없는 코드(UT_TYPES/UT_DB_CODE 미등록)는 DB 이름을 그대로 쓰고 단위를 덧붙이지
 * 않는다 — 그 이름에 단위가 들어 있을 수 있어 또 겹치기 때문이다.
 */
const UNIT_SUFFIX = /\s*\[[^\]]*\]\s*$/;

/** 기계 항목 헤더 — `CDA [Nl/min]` */
const mechHeader = (i: RptSumItem): string => findUtType(i.utmTpCd)?.label ?? i.utmTpNm;

/** 단위를 뗀 항목명 — `Return air [CMH]` → `Return air`. 전기는 공정명을 그대로 쓴다 */
const itemName = (i: RptSumItem): string =>
  i.atcDivsCd === 'ELEC'
    ? i.utmTpNm
    : findUtType(i.utmTpCd)?.label.replace(UNIT_SUFFIX, '') ?? i.utmTpNm;

/** 단위 한 칸 — 기계는 FE 상수, 전기는 전부 MW(표 헤더 '전기 [MW]'와 같은 기준) */
const itemUnit = (i: RptSumItem): string =>
  i.atcDivsCd === 'ELEC' ? 'MW' : utUnitOf(i.utmTpCd) || i.msrUnitCd || '-';

const headerCellOpts = { bold: true, fill: { color: HEADER_FILL }, align: 'center' as const };
const border = { type: 'solid' as const, color: BORDER_COLOR, pt: 0.75 };

/** LAYOUT_WIDE(13.33 × 7.5인치)의 세로 길이와 하단 여백 */
const SLIDE_H = 7.5;
const BOTTOM_MARGIN = 0.3;

/** fontSize 10의 한 줄 높이(줄간격 포함)와 셀 위아래 여백 */
const LINE_H = 0.17;
const CELL_PAD = 0.1;
/** 추정 오차 보정 — 조금 넘치는 것보다 덜 채우는 게 낫다 */
const HEIGHT_SAFETY = 1.08;

/**
 * fontSize 10 기준 문자열의 대략적인 가로 폭(inch).
 * 1인치에 반각(영문·숫자)은 약 11자, 전각(한글)은 약 6자가 들어간다고 본다.
 */
const textW = (text: string): number => {
  let wide = 0;
  for (let n = 0; n < text.length; n += 1) if (text.charCodeAt(n) > 0x7f) wide += 1;
  return (text.length - wide) / 11 + wide / 6;
};

/**
 * 셀 텍스트가 주어진 열 폭에서 몇 줄로 감길지 추정한다.
 *
 * ⚠ **단어 단위로 세야 한다.** PowerPoint는 공백에서 줄을 나누므로, 전체 폭을 열 폭으로
 *   나누는 방식은 실제보다 적게 나온다 — `Return air [CMH]`는 0.9인치 열에서 2줄이 아니라
 *   3줄이다(`Return` / `air` / `[CMH]`). 이 오차 때문에 ②가 ① 위로 겹쳤다.
 *
 * 열보다 긴 단어는 중간에서 강제로 쪼개진다고 본다.
 *
 * 폰트 메트릭 없이 정확히는 알 수 없으므로 **겹침을 막는 쪽으로** 쓴다. 이 값은 레이아웃
 * 좌표 계산에만 관여하고, 표 자체의 줄바꿈은 pptxgenjs가 한다.
 */
const estLines = (text: string, colW: number): number => {
  if (colW <= 0) return 1;
  const words = String(text ?? '')
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return 1;

  const spaceW = textW(' ');
  let lines = 1;
  let used = 0;
  words.forEach((word) => {
    const w = textW(word);
    if (w > colW) {
      // 열보다 긴 단어 — 현재 줄을 닫고 쪼개진 만큼 줄을 더한다
      if (used > 0) lines += 1;
      lines += Math.ceil(w / colW) - 1;
      used = w % colW;
      return;
    }
    const gap = used > 0 ? spaceW : 0;
    if (used + gap + w > colW) {
      lines += 1;
      used = w;
    } else {
      used += gap + w;
    }
  });
  return Math.max(1, lines);
};

/** 한 행의 실제 높이 추정 — 가장 많이 감기는 셀이 행 높이를 정한다 */
const estRowH = (cells: { text: string; colW: number }[], minH: number): number => {
  const lines = cells.reduce((max, c) => Math.max(max, estLines(c.text, c.colW)), 1);
  return Math.max(minH, lines * LINE_H + CELL_PAD);
};

/**
 * "분석 Report" 탭의 "보고서 발행"(PPT) — 제목/부제 + 용량 정합화 표(①) + 검토 내역 표(②).
 * 화면에 현재 표시 중인(RPOT_TGT_YN='Y') 전기/기계 UT 항목만 포함한다.
 *
 * ref/frontend/features/UtMatrixRpt/utils/exportPpt.ts에서 옮겼다.
 *
 * 검토 내역 표(②)는 autoPage로 다음 장에 이어 그린다. 내역(RVW_CTN)이 VARCHAR(4000)이라
 * 한 행만 길어도 표가 세로로 부푸는데, rowH는 최소 높이일 뿐이라 행 높이를 줄여도 막히지
 * 않는다. 한 장에 욱여넣던 기존 방식(autoPage:false)은 넘친 행이 슬라이드 밖으로 잘려
 * 아예 안 보였다. 대신 분할 지점은 pptxgenjs가 정하므로 장수는 내용에 따라 달라진다.
 *
 * ①은 항목이 많아도 한 장에 유지한다 — 전/후/차이를 나란히 봐야 하는 표라 쪼개면 읽기
 * 어렵다. 그래서 **항목이 많으면 ②를 다음 장에서 시작한다**(fitsOnFirstSlide).
 * 항목 수 기준(MAX_ITEMS_ON_FIRST_SLIDE)과 높이 추정 중 하나라도 걸리면 넘긴다.
 *
 * ⚠ ②의 시작 좌표를 ①의 높이(table1H)에서 계산하므로 ①에 autoPage를 걸면 좌표가
 *   어긋난다. table1H는 줄바꿈까지 추정해 구한다 — rowH가 최소 높이일 뿐이어서 항목이
 *   많아 열이 좁아지면 실제 표가 더 높아지고, 그걸 빼먹으면 두 표가 겹친다. 추정은
 *   글자수 기반이라 오차가 있어서 항목 수 기준을 함께 둔다.
 */
export async function exportUtMatrixRptPpt(
  rptNm: string,
  targets: RptUtmTarget[],
  elecItems: RptSumItem[],
  mechItems: RptSumItem[]
): Promise<void> {
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';

  const slide = pptx.addSlide();
  slide.background = { color: 'FFFFFF' };

  const orderedTargets = ROLE_ORDER.map((role) => targets.find((t) => t.role === role)).filter(
    (t): t is RptUtmTarget => !!t
  );
  const mst = targets.find((t) => t.role === 'MST');
  const tgt = targets.find((t) => t.role === 'TGT');
  const subtitle =
    mst && tgt ? `${mst.utmNm}(${mst.verNo}) vs ${tgt.utmNm}(${tgt.verNo}) 비교` : rptNm;

  const now = new Date();
  const yyyyMm = `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}`;

  slide.addText(subtitle, {
    x: 0.13,
    y: 0.13,
    w: 9,
    h: 0.4,
    fontSize: 18,
    bold: true,
    underline: { style: 'sng' },
    fontFace: FONT,
    color: '000000',
  });
  slide.addText(`유틸리티 기술팀(${yyyyMm})`, {
    x: 10.8,
    y: 0.06,
    w: 2.4,
    h: 0.27,
    fontSize: 10,
    underline: { style: 'sng' },
    fontFace: FONT,
    color: '000000',
    align: 'right',
  });

  // ── ① UT Matrix 용량 정합화 ──
  const section1Y = 0.56;
  slide.addText('■ UT Matrix 용량 정합화', {
    x: 0.21,
    y: section1Y,
    w: 5,
    h: 0.32,
    fontSize: 14,
    bold: true,
    fontFace: FONT,
    color: '000000',
  });

  // 전기 항목이 있으면 "전기 [MW]" 묶음 헤더가 생겨 헤더가 2줄이 된다
  const numHeaderRows = elecItems.length > 0 ? 2 : 1;
  const items = [...elecItems, ...mechItems];
  const table1Row1: PptxGenJS.TableRow = [
    { text: '구분', options: { ...headerCellOpts, rowspan: numHeaderRows } },
    { text: 'UT Matrix', options: { ...headerCellOpts, rowspan: numHeaderRows } },
    { text: '라인', options: { ...headerCellOpts, rowspan: numHeaderRows } },
    ...(elecItems.length > 0
      ? [{ text: '전기 [MW]', options: { ...headerCellOpts, colspan: elecItems.length } }]
      : []),
    ...mechItems.map((i) => ({
      text: mechHeader(i),
      options: { ...headerCellOpts, rowspan: numHeaderRows },
    })),
  ];
  const table1Rows: PptxGenJS.TableRow[] = [table1Row1];
  if (numHeaderRows === 2) {
    table1Rows.push(elecItems.map((i) => ({ text: i.utmTpNm, options: headerCellOpts })));
  }
  orderedTargets.forEach((t) => {
    table1Rows.push([
      { text: ROLE_LABEL[t.role], options: { align: 'center' } },
      { text: `${t.utmNm}(${t.verNo})`, options: { align: 'left' } },
      { text: t.prdnLnNm || '-', options: { align: 'center' } },
      ...items.map((i) => ({ text: fmt(i.values[t.role]), options: { align: 'right' as const } })),
    ]);
  });
  table1Rows.push([
    { text: '차이(기준-비교대상)', options: { bold: true, colspan: 3 } },
    ...items.map((i) => {
      const diff = rptSumDiff(i);
      return {
        text: fmt(diff),
        options: { align: 'right' as const, bold: true, color: (diff ?? 0) < 0 ? RED : GRAY },
      };
    }),
  ]);

  const labelW1 = [0.78, 2.5, 0.83];
  const itemColW1 =
    items.length > 0 ? (12.77 - labelW1.reduce((a, b) => a + b, 0)) / items.length : 0;
  const table1RowH = 0.32;
  /*
    항목이 많아지면 항목 열이 좁아져 글자가 여러 줄로 감긴다. rowH는 **최소 높이**라 표는
    그만큼 실제로 더 높아지는데, 예전에는 table1Rows.length * rowH 로만 계산해서 ②의
    y좌표가 실제 표 아래보다 위로 잡혔다 — 그래서 두 표가 겹쳐 보였다.

    감기는 칸은 세 군데다 — 기계 항목 헤더(`CDA [Nl/min]`), 전기 공정명, 그리고 본문의
    UT Matrix 이름. 행마다 가장 많이 감기는 칸이 그 행의 높이를 정한다.
  */
  const headerTexts = [...mechItems.map((i) => mechHeader(i)), ...elecItems.map((i) => i.utmTpNm)];
  const headerLines = headerTexts.reduce((max, t) => Math.max(max, estLines(t, itemColW1)), 1);
  const headerBlockH = Math.max(numHeaderRows * table1RowH, headerLines * LINE_H + CELL_PAD);
  const bodyH = orderedTargets.reduce(
    (sum, t) =>
      sum +
      estRowH(
        [
          { text: `${t.utmNm}(${t.verNo})`, colW: labelW1[1] },
          { text: t.prdnLnNm || '-', colW: labelW1[2] },
        ],
        table1RowH
      ),
    0
  );
  // 마지막 '차이(기준-비교대상)' 행은 colspan 3(=4.11인치)이라 감기지 않는다
  const table1H = (headerBlockH + bodyH + table1RowH) * HEIGHT_SAFETY;

  slide.addTable(table1Rows, {
    x: 0.28,
    y: section1Y + 0.34,
    w: 12.77,
    rowH: table1RowH,
    border,
    fontSize: 10,
    fontFace: FONT,
    color: '000000',
    colW: [...labelW1, ...items.map(() => itemColW1)],
  });

  // ── ② UT 사용량 정합화 검토 내역 ──
  /*
    ①이 커지면 ②를 **다음 장에서 시작한다.**

    ①을 여러 장으로 쪼개지는 않는다 — 전/후/차이를 나란히 봐야 의미가 있는 표라
    가로로도 세로로도 끊으면 읽기 어려워진다. 대신 ②를 새 장으로 내린다.

    판정 기준이 둘이고, **하나라도 걸리면 넘긴다.**

    ① 항목 수 — MAX_ITEMS_ON_FIRST_SLIDE를 넘으면 무조건 넘긴다. 아래 높이 추정은
       폰트 메트릭 없이 글자수로 계산하는 값이라 오차가 있고, 항목이 많을수록 열이 좁아져
       오차도 커진다. 항목이 많을 때는 추정을 믿지 않고 그냥 장을 나누는 편이 안전하다.
    ② 추정 높이 — "제목 + 헤더 + 최소 3행"이 남지 않으면 자리가 없는 것으로 본다.
       한두 행만 걸치면 autoPage가 어차피 다음 장으로 넘겨서 첫 장에 제목만 남는다.
  */
  const MAX_ITEMS_ON_FIRST_SLIDE = 8;
  const MIN_SECTION2_H = 0.34 + 0.08 + 0.28 * 4; // 제목 + 여백 + 헤더행 + 3행
  const section2YOnFirst = section1Y + 0.34 + table1H + 0.12;
  const fitsOnFirstSlide =
    items.length <= MAX_ITEMS_ON_FIRST_SLIDE &&
    section2YOnFirst + MIN_SECTION2_H <= SLIDE_H - BOTTOM_MARGIN;

  const slide2 = fitsOnFirstSlide ? slide : pptx.addSlide();
  if (!fitsOnFirstSlide) slide2.background = { color: 'FFFFFF' };
  const section2Y = fitsOnFirstSlide ? section2YOnFirst : 0.4;

  slide2.addText('■ UT 사용량 정합화 검토 내역', {
    x: 0.13,
    y: section2Y,
    w: 5,
    h: 0.32,
    fontSize: 14,
    bold: true,
    fontFace: FONT,
    color: '000000',
  });

  const table2Y = section2Y + 0.34 + 0.08;
  const table2Rows: PptxGenJS.TableRow[] = [
    [
      { text: '구분', options: headerCellOpts },
      { text: 'UT 항목', options: headerCellOpts },
      { text: '단위', options: headerCellOpts },
      { text: 'UT 부족량', options: headerCellOpts },
      { text: '투자비 영향', options: headerCellOpts },
      { text: '내역', options: headerCellOpts },
      { text: '향후 계획', options: headerCellOpts },
    ],
    ...items.map(
      (i): PptxGenJS.TableRow => [
        { text: i.atcDivsCd === 'ELEC' ? '전기' : '기계', options: { align: 'center' } },
        { text: itemName(i), options: { align: 'left' } },
        { text: itemUnit(i), options: { align: 'center' } },
        {
          text: fmt(rptSumDiff(i)),
          options: { align: 'right' as const, color: (rptSumDiff(i) ?? 0) < 0 ? RED : GRAY },
        },
        { text: i.infcRvwCtn || '-', options: { align: 'left' } },
        { text: i.rvwCtn || '-', options: { align: 'left' } },
        { text: i.nextPlanCtn || '-', options: { align: 'left' } },
      ]
    ),
  ];

  /*
    한 장에 맞추려고 행 높이를 압축하지 않는다 — 긴 내역 앞에서는 어차피 무력하고,
    글자만 작아져 읽기 어려워진다. 넘치는 행은 다음 장으로 넘긴다.

    ⚠ autoPage 보조 옵션은 pptxgenjs 타입 정의에 버전별로 누락돼 있어 그대로 쓰면
      컴파일이 막힌다. 런타임에는 정상 동작하므로 타입 검사를 피해 따로 넘긴다.
  */
  const autoPageOpts: any = {
    autoPage: true,
    // 이어지는 장에서도 어떤 열인지 알 수 있어야 한다
    autoPageRepeatHeader: true,
    // 2장부터는 위 두 표가 없으므로 상단 여백만 두고 시작한다
    autoPageSlideStartY: 0.4,
  };

  slide2.addTable(table2Rows, {
    x: 0.3,
    y: table2Y,
    w: 12.74,
    rowH: 0.28,
    border,
    fontSize: 9,
    fontFace: FONT,
    color: '000000',
    colW: [0.55, 0.9, 0.62, 0.88, 1.66, 4.49, 3.64],
    ...autoPageOpts,
  });

  await pptx.writeFile({ fileName: `${rptNm || 'UT_Matrix_Rpt'}.pptx` });
}
